import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconCloudOff,
  IconCloudUpload,
  IconFlag,
  IconMapPin,
  IconPlayerSkipForward,
} from '@tabler/icons-react';
import { PROCEDURES_ERROR_CODES, PROCEDURES_RUN_STATUS, PROCEDURES_RUN_STEP_STATUS } from '@actschurch/ccms-sdk';
import { ProcedureAccessError } from '@/components/procedure/AccessScreens';
import { AskSheet } from '@/components/procedure/AskSheet';
import { CalloutList } from '@/components/procedure/ProcedureCallout';
import { ReportProblemSheet } from '@/components/procedure/ReportProblemSheet';
import { StepChecks } from '@/components/procedure/StepChecks';
import { StepMedia, prefetchImages } from '@/components/media/StepMedia';
import { EmptyState, LoadingState, StatusPill, StickyActionBar } from '@/components/ui';
import { formatClock, utcStamp } from '@/lib/format';
import { isNetworkError, isNotFound, procedureErrorCode } from '@/lib/errors';
import { usePortalMode, useProcedureView, useRunView, type PortalMode } from '@/lib/portalApi';
import { buildRunnerModel } from '@/lib/runMerge';
import { ensureMemberRecord } from '@/lib/runLifecycle';
import { getDrafts, getRunRecord, markRunFinished, saveDraft, type LocalRunRecord } from '@/lib/runStore';
import { hasText, sanitizeHtml } from '@/lib/sanitize';
import { enqueueFinish, enqueueStep, useRunPending, useSyncStore } from '@/lib/syncQueue';

const { Done: DONE, Skipped: SKIPPED, Problem: PROBLEM } = PROCEDURES_RUN_STEP_STATUS;

/** "We couldn't find your progress" (token gone from this phone, or a run from another account). */
function LostRun({ code, signedInRun }: { code: string; signedInRun?: boolean }) {
  return (
    <div className="proc-main">
      <EmptyState
        icon={<IconFlag size={28} />}
        title="We couldn't find this run on this phone"
        action={<Button component={Link} to={`/p/${encodeURIComponent(code)}`} fullWidth>Start the procedure again</Button>}
        secondaryAction={
          signedInRun ? (
            <Button component={Link} to={`/auth/login?redirect=${encodeURIComponent(window.location.pathname)}`} variant="subtle" fullWidth>
              Sign in to carry on
            </Button>
          ) : undefined
        }
      >
        It may have been started on another phone or in another browser. You can start the procedure again from the beginning.
      </EmptyState>
    </div>
  );
}

/**
 * /p/:code/run/:runId?step=N: one step per screen. Done, Skip (optional steps) and Report a problem
 * sit in the sticky bar; every tick is saved on the phone first and synced when there is signal.
 */
export default function RunnerPage() {
  const { code = '', runId: runIdParam } = useParams();
  const runId = Number(runIdParam);
  const validRunId = Number.isFinite(runId) && runId > 0;
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { mode: sessionMode, userId, isInitializing } = usePortalMode();
  const [record, setRecord] = useState<LocalRunRecord | null>(() => (validRunId ? getRunRecord(runId) : null));
  const runMode: PortalMode | null = record?.mode ?? (sessionMode === 'member' && !isInitializing ? 'member' : null);
  const viewQuery = useProcedureView(code);
  const runQuery = useRunView(validRunId ? runId : null, runMode);
  const pending = useRunPending(validRunId ? runId : null);
  const online = useSyncStore((s) => s.online);
  const [drafts, setDrafts] = useState(() => (validRunId ? getDrafts(runId) : {}));
  const [problemOpen, setProblemOpen] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const lastRenew = useRef(0);
  const firstRender = useRef(true);

  const view = viewQuery.data?.view;
  const run = runQuery.data ?? null;

  // A signed-in run this phone has not seen yet (opened from My runs): remember it.
  useEffect(() => {
    if (!record && run && runMode === 'member') {
      setRecord(
        ensureMemberRecord({
          runId,
          code,
          title: run.procedureTitle,
          userId,
          startedAt: run.startedAt,
          active: run.statusID === PROCEDURES_RUN_STATUS.InProgress,
        }),
      );
    }
  }, [record, run, runMode, runId, code, userId]);

  const model = useMemo(() => buildRunnerModel(view, run, pending), [view, run, pending]);

  const stepParam = Number(params.get('step'));
  const index = model.total === 0
    ? 0
    : Number.isFinite(stepParam) && stepParam >= 1
      ? Math.min(stepParam, model.total) - 1
      : model.firstOpenIndex;

  // Keep ?step=N in the address so a reload (or a shared link) opens the same step.
  useEffect(() => {
    if (model.total > 0 && String(index + 1) !== params.get('step')) {
      const next = new URLSearchParams(params);
      next.set('step', String(index + 1));
      setParams(next, { replace: true });
    }
  }, [index, model.total, params, setParams]);

  // Move focus to the step title on every step change (screen readers announce the new step).
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    headingRef.current?.focus();
    window.scrollTo(0, 0);
  }, [index]);

  // Warm the photo cache for every step once the procedure is here.
  useEffect(() => {
    if (!view || viewQuery.data?.offline) return;
    const urls: (string | null | undefined)[] = [];
    view.steps.forEach((s) => s.media.forEach((m) => urls.push(m.url, m.thumbUrl)));
    prefetchImages(urls);
  }, [view, viewQuery.data?.offline]);

  const goTo = useCallback(
    (i: number) => {
      const next = new URLSearchParams(params);
      next.set('step', String(Math.max(0, Math.min(i, Math.max(0, model.total - 1))) + 1));
      setParams(next);
    },
    [params, setParams, model.total],
  );

  const renew = useCallback(() => {
    if (Date.now() - lastRenew.current < 60_000) return;
    lastRenew.current = Date.now();
    void viewQuery.refetch();
  }, [viewQuery]);

  // ---------------------------------------------------------------- guards
  if (!validRunId) return <Navigate to={`/p/${encodeURIComponent(code)}`} replace />;
  if (isInitializing) return <div className="proc-main"><LoadingState variant="spinner" label="Opening your run" /></div>;
  if (!runMode) return <LostRun code={code} />;

  const runError = runQuery.error;
  const runErrCode = runError ? procedureErrorCode(runError) : null;
  if (!run && runError && !isNetworkError(runError)) {
    if (runErrCode === PROCEDURES_ERROR_CODES.RunTokenInvalid || isNotFound(runError)) {
      return <LostRun code={code} signedInRun={runMode === 'member'} />;
    }
    return <ProcedureAccessError code={code} error={runError} onRetry={() => void runQuery.refetch()} retrying={runQuery.isFetching} />;
  }
  if (!view) {
    if (!viewQuery.isError) return <div className="proc-main"><LoadingState label="Opening the procedure" count={2} /></div>;
    return <ProcedureAccessError code={code} error={viewQuery.error} onRetry={() => void viewQuery.refetch()} retrying={viewQuery.isFetching} />;
  }

  const finishedLocally = record?.state === 'completed' || model.finishPending;
  if (finishedLocally || (run?.statusID === PROCEDURES_RUN_STATUS.Completed && pending.length === 0)) {
    return <Navigate to={`/p/${encodeURIComponent(code)}/done/${runId}`} replace />;
  }
  if (record?.state === 'abandoned' || model.abandonPending || run?.statusID === PROCEDURES_RUN_STATUS.Abandoned) {
    return (
      <div className="proc-main">
        <EmptyState
          icon={<IconFlag size={28} />}
          title="This run was set aside"
          action={<Button component={Link} to={`/p/${encodeURIComponent(code)}`} fullWidth>Back to the procedure</Button>}
        >
          It was stopped before the end (or left for too long). Start the procedure again to carry on.
        </EmptyState>
      </div>
    );
  }
  if (model.total === 0) {
    return (
      <div className="proc-main">
        <EmptyState icon={<IconFlag size={28} />} title="This procedure has no steps yet" action={<Button component={Link} to="/" fullWidth>All procedures</Button>} />
      </div>
    );
  }

  // ---------------------------------------------------------------- the step on screen
  const step = model.steps[index];
  const content = step.content;
  const checks = content?.checks ?? [];
  const draftChecks = drafts[String(step.stepID)] ?? step.checkedIds;
  const isLast = index === model.total - 1;
  const optional = !!content?.isOptional;
  const runCode = record?.code ?? code;
  const pct = Math.round((model.answered / Math.max(1, model.total)) * 100);

  const commit = (statusID: number | null, note?: string | null, checkedIds: number[] = draftChecks) => {
    enqueueStep({
      runId,
      code: runCode,
      mode: runMode,
      stepId: step.stepID,
      request: {
        statusID,
        checkedIds: statusID ? checkedIds : [],
        note: note ?? null,
        clientCheckedAt: statusID ? utcStamp() : null,
      },
    });
  };

  const toggleCheck = (checkId: number) => {
    const next = draftChecks.includes(checkId) ? draftChecks.filter((x) => x !== checkId) : [...draftChecks, checkId];
    setDrafts((d) => ({ ...d, [String(step.stepID)]: next }));
    saveDraft(runId, step.stepID, next);
    // Already marked: keep the saved ticks in step with what is on screen.
    if (step.statusID) commit(step.statusID, step.note, next);
  };

  const doFinish = () => {
    enqueueFinish({ runId, code: runCode, mode: runMode, kind: 'complete', request: {} });
    markRunFinished(runId, 'completed');
    navigate(`/p/${encodeURIComponent(code)}/done/${runId}`, { replace: true });
  };

  const finish = () => {
    const open = model.steps.filter((s) => s.stepID !== step.stepID && !s.statusID && !s.content?.isOptional);
    if (open.length === 0) {
      doFinish();
      return;
    }
    const first = open[0];
    modals.openConfirmModal({
      title: open.length === 1 ? '1 step is not ticked off yet' : `${open.length} steps are not ticked off yet`,
      children: (
        <div>
          <Text mb="xs">Finish anyway, or go back and do them first?</Text>
          <ul className="proc-openlist">
            {open.slice(0, 6).map((s) => <li key={s.stepID}>Step {s.number}: {s.title}</li>)}
            {open.length > 6 && <li>and {open.length - 6} more</li>}
          </ul>
        </div>
      ),
      labels: { confirm: 'Finish anyway', cancel: `Go to step ${first.number}` },
      confirmProps: { size: 'md' },
      cancelProps: { size: 'md', variant: 'filled' },
      onConfirm: doFinish,
      onCancel: () => goTo(first.number - 1),
    });
  };

  const advance = () => {
    if (isLast) finish();
    else goTo(index + 1);
  };

  const markDone = () => {
    const unticked = checks.filter((c) => !draftChecks.includes(c.checkID)).length;
    const proceed = () => {
      commit(DONE);
      advance();
    };
    if (unticked === 0) {
      proceed();
      return;
    }
    modals.openConfirmModal({
      title: unticked === 1 ? '1 check is not ticked' : `${unticked} checks are not ticked`,
      children: <Text>Please make sure everything on the list is right before you carry on.</Text>,
      labels: { confirm: 'Mark done anyway', cancel: 'Go back to the checks' },
      confirmProps: { size: 'md' },
      cancelProps: { size: 'md', variant: 'filled' },
      onConfirm: proceed,
    });
  };

  const skip = () => {
    commit(SKIPPED);
    advance();
  };

  const undo = () => commit(null);

  const reportProblem = (note: string) => {
    commit(PROBLEM, note);
    setProblemOpen(false);
    notifications.show({
      color: 'red',
      title: 'Problem reported',
      message: "Thanks. We've let the team know. You can carry on, or wait for help.",
      autoClose: 5000,
    });
  };

  const statusLine = (() => {
    if (!step.statusID) return null;
    const when = step.checkedAt ? ` at ${formatClock(step.checkedAt)}` : '';
    const saved = step.pending ? ' Saved on this phone.' : '';
    if (step.statusID === DONE) {
      return { tone: 'ok', text: `Done${when}.${saved}` };
    }
    if (step.statusID === SKIPPED) return { tone: 'neutral', text: `Skipped${when}.${saved}` };
    return { tone: 'bad', text: `Problem reported${when}: ${step.note ?? ''}${saved}` };
  })();

  // Sync status for this run, read out politely from the action bar (where the thumb already is).
  const waitingToSync = pending.length > 0 && (!online || pending.some((e) => e.attempts > 0));
  const syncHint =
    pending.length === 0 ? undefined : (
      <span className={`proc-synchint${waitingToSync ? ' waiting' : ''}`}>
        {waitingToSync ? <IconCloudOff size={16} aria-hidden="true" /> : <IconCloudUpload size={16} aria-hidden="true" />}
        {waitingToSync ? 'Saved on this phone, will sync' : 'Saving...'}
      </span>
    );

  return (
    <div className="proc-main proc-runner proc-hasbar">
      <div className="proc-pagebody">
      <div className="proc-runhead">
        <div className="top">
          <button
            type="button"
            className="proc-backlink"
            onClick={() => (index > 0 ? goTo(index - 1) : navigate(`/p/${encodeURIComponent(code)}`))}
          >
            <IconArrowLeft size={20} aria-hidden="true" /> {index > 0 ? 'Previous' : 'Overview'}
          </button>
          <span className="count">Step {step.number} of {model.total}</span>
        </div>
        <div
          className="proc-progress"
          role="progressbar"
          aria-label={`${model.answered} of ${model.total} steps ticked off`}
          aria-valuemin={0}
          aria-valuemax={model.total}
          aria-valuenow={model.answered}
        >
          <span style={{ width: `${pct}%` }} />
        </div>
      </div>

      <p className="proc-eyebrow proc-runtitle">{view.title}</p>
      <h1 className="proc-title proc-steptitle" ref={headingRef} tabIndex={-1}>
        <span className="sr-only">Step {step.number} of {model.total}: </span>
        {step.title}
      </h1>
      {optional && <div className="proc-optional"><StatusPill tone="neutral" dot={false}>Optional step</StatusPill></div>}

      {content?.whereText && (
        <p className="proc-where">
          <IconMapPin size={20} aria-hidden="true" />
          <span><b>Where:</b> {content.whereText}</span>
        </p>
      )}

      {!content && <p className="proc-muted">This step changed after you started. Do what you can, or report a problem.</p>}

      {content && content.media.length > 0 && (
        <StepMedia media={content.media} label={`Step ${step.number}: ${step.title}`} onExpired={renew} />
      )}

      {content && hasText(content.bodyHtml) && (
        <div className="proc-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(content.bodyHtml) }} />
      )}

      {content && content.callouts.length > 0 && <CalloutList callouts={content.callouts} />}

      <StepChecks checks={checks} checked={draftChecks} onToggle={toggleCheck} />

      {statusLine && (
        <div className={`proc-stepstatus ${statusLine.tone}`} role="status">
          <span className="msg">{statusLine.text}</span>
          <span className="acts">
            {step.statusID === PROBLEM && (
              <button type="button" className="proc-textbtn" onClick={() => setProblemOpen(true)}>Change note</button>
            )}
            <button type="button" className="proc-textbtn" onClick={undo}>Undo</button>
          </span>
        </div>
      )}

      <nav className="proc-stepnav" aria-label="Steps">
        <button type="button" className="proc-textbtn" onClick={() => goTo(index - 1)} disabled={index === 0}>
          <IconArrowLeft size={18} aria-hidden="true" /> Previous step
        </button>
        {!isLast && (
          <button type="button" className="proc-textbtn" onClick={() => goTo(index + 1)}>
            Look at the next step <IconArrowRight size={18} aria-hidden="true" />
          </button>
        )}
      </nav>

      {view.aiChatAvailable && (
        <AskSheet
          code={code}
          title={view.title}
          mode={sessionMode}
          runId={runMode === sessionMode ? runId : null}
          runToken={runMode === 'public' && sessionMode === 'public' ? record?.token ?? null : null}
          stepId={step.stepID}
          stepNumber={step.number}
          onGoToStep={(n) => goTo(n - 1)}
          lift="runner"
        />
      )}

      <ReportProblemSheet
        opened={problemOpen}
        onClose={() => setProblemOpen(false)}
        stepLabel={`Step ${step.number}: ${step.title}`}
        initialNote={step.statusID === PROBLEM ? step.note : null}
        onSubmit={reportProblem}
      />
      </div>

      <StickyActionBar hint={syncHint}>
        <div className="proc-runbar">
          {!step.statusID ? (
            <Button size="xl" fullWidth className="proc-donebtn" leftSection={<IconCheck size={26} aria-hidden="true" />} onClick={markDone}>
              {isLast ? 'Done and finish' : 'Done'}
            </Button>
          ) : (
            <Button size="xl" fullWidth className="proc-donebtn" rightSection={<IconArrowRight size={24} aria-hidden="true" />} onClick={advance}>
              {isLast ? 'Finish' : 'Next step'}
            </Button>
          )}
          <div className="secondary">
            {!step.statusID && optional && (
              <Button variant="default" leftSection={<IconPlayerSkipForward size={18} aria-hidden="true" />} onClick={skip}>
                Skip
              </Button>
            )}
            {step.statusID !== PROBLEM && (
              <Button variant="light" color="red" leftSection={<IconAlertTriangle size={18} aria-hidden="true" />} onClick={() => setProblemOpen(true)}>
                Report a problem
              </Button>
            )}
          </div>
        </div>
      </StickyActionBar>
    </div>
  );
}
